import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import ProjectCard, { Project } from './ProjectCard';
import CreateProjectModal from './CreateProjectModal';
import CertificateHoldersPanel from './acord/CertificateHoldersPanel';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import EditProjectModal from './EditProjectModal';
import DeleteProjectModal from './DeleteProjectModal';
import AMS360ActivityModal from './AMS360ActivityModal';
import BatchRenameModal, { ProjectDropFile } from './BatchRenameModal';
import { useProjectFileDrop } from '../hooks/useProjectFileDrop';
import { projectService } from '../services/projectService';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import SparkleIcon from './icons/SparkleIcon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface ProjectManagementProps {
  customerId: string;
  customerName: string;
  onProjectSelect: (project: Project, opts?: { forceWorkflowsTab?: boolean }) => void;
  onBack: () => void;
  ams360CustomerId?: string | null;
  isAms360Enabled?: boolean;
}

// Small "N recommended workflows" pill — shared by card and list-row
// layouts. Clicking it opens the project straight to its Workflows tab.
const RecommendationPill: React.FC<{ count: number; onOpen: (e: React.MouseEvent) => void }> = ({ count, onOpen }) => {
  if (count <= 0) return null;
  return (
    <button
      onClick={onOpen}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
      title={`${count} recommended workflow${count === 1 ? '' : 's'}`}
    >
      <SparkleIcon className="w-3 h-3" />
      {count}
    </button>
  );
};

type ViewMode = 'card' | 'list';

const VIEW_MODE_KEY = 'riskninja-project-view-mode';

const typeLabels: Record<string, string> = {
  new_business: 'New Business',
  renewal: 'Renewal',
  loss_run: 'Loss Run',
  year_over_year: 'Year Over Year',
  comparison: 'Comparison',
  insurance_requirements: 'Insurance Requirements',
  other: 'Other',
};

const statusBadge: Record<string, string> = {
  planning: 'text-yellow-700 bg-yellow-50 dark:text-yellow-300 dark:bg-yellow-900/20',
  active: 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/20',
  completed: 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20',
  on_hold: 'text-orange-700 bg-orange-50 dark:text-orange-300 dark:bg-orange-900/20',
  cancelled: 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/20',
};

// A single list-view row. Extracted to module scope (not a closure inside
// ProjectManagement's render) so useProjectFileDrop's hook state survives
// re-renders — one hook instance per row, driving that row's own drag
// highlight, while the actual rename modal renders once at the list level.
interface ProjectListRowProps {
  project: Project;
  depth: number;
  subCount: number;
  isExpanded: boolean;
  suggestionCount: number;
  onToggleExpand: (id: string) => void;
  onSelect: (project: Project) => void;
  onOpenWorkflows: (project: Project) => void;
  onAddSubfolder: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  onDocumentsUploaded: () => void;
  onFilesDropped: (project: Project, files: File[], performUpload: (rows: ProjectDropFile[]) => Promise<void>) => void;
}

const ProjectListRow: React.FC<ProjectListRowProps> = ({
  project,
  depth,
  subCount,
  isExpanded,
  suggestionCount,
  onToggleExpand,
  onSelect,
  onOpenWorkflows,
  onAddSubfolder,
  onEdit,
  onDelete,
  onDocumentsUploaded,
  onFilesDropped,
}) => {
  const { dragProps, isDragging } = useProjectFileDrop({
    project,
    onDocumentsUploaded,
    onFilesDropped: (files, performUpload) => onFilesDropped(project, files, performUpload),
  });

  return (
    <tr
      {...dragProps}
      className={`cursor-pointer group ${isDragging ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-primary ring-inset' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
      onClick={() => onSelect(project)}
    >
      <td className="px-4 py-2">
        <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 20}px` }}>
          {/* Expand/collapse caret (or spacer to keep names aligned) */}
          {subCount > 0 ? (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleExpand(project.id); }}
              className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
              title={isExpanded ? 'Collapse' : 'Expand sub-folders'}
              aria-expanded={isExpanded}
            >
              <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <span className="w-5 h-5 inline-block" />
          )}
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
          </svg>
          <span className="font-medium text-gray-900 dark:text-white truncate">{project.title}</span>
          {subCount > 0 && (
            <span className="ml-1 text-xs text-amber-700 dark:text-amber-300">({subCount})</span>
          )}
          <RecommendationPill
            count={suggestionCount}
            onOpen={(e) => { e.stopPropagation(); onOpenWorkflows(project); }}
          />
          {isDragging && (
            <span className="ml-2 text-xs font-medium text-primary whitespace-nowrap">Drop to upload to {project.title}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{typeLabels[project.type] || project.type}</td>
      <td className="px-4 py-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[project.status] || ''}`}>
          {project.status}
        </span>
      </td>
      <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{project.createdByName || '—'}</td>
      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
        {project.updatedAt ? format(new Date(project.updatedAt), 'MMM d, yyyy') : '—'}
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onAddSubfolder(project); }}
            className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
            title="Add sub-folder"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v4M10 13h4" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(project); }}
            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(project); }}
            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};

const ProjectManagement: React.FC<ProjectManagementProps> = ({
  customerId,
  customerName,
  onProjectSelect,
  onBack,
  ams360CustomerId,
  isAms360Enabled = false
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHolders, setShowHolders] = useState(false);
  const features = useFeatureFlags();
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAMS360ActivityModal, setShowAMS360ActivityModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  // Card vs list (file-explorer) view, persisted across sessions
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    return saved === 'list' ? 'list' : 'card';
  });
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // Folder-tree navigation: which folder we're currently inside (null = top level)
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string; title: string }[]>([]);

  // When creating, this captures the parent folder for the new project (null = top level)
  const [createParent, setCreateParent] = useState<{ id: string | null; title?: string }>({ id: null });

  // Drag-and-drop upload in list view: each row owns its own drag/highlight
  // state (via useProjectFileDrop), but the actual rename modal renders once
  // here, driven by whichever row's drop most recently supplied files.
  const [activeDrop, setActiveDrop] = useState<{
    project: Project;
    files: File[];
    performUpload: (rows: ProjectDropFile[]) => Promise<void>;
  } | null>(null);

  // Inline-expanded folders in list view (tree drop-down via caret)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Fetch projects for the customer (flat list across all nesting levels)
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProjects(customerId);
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    // Reset navigation when switching customers
    setCurrentParentId(null);
    setBreadcrumb([]);
  }, [customerId]);

  // Recommendation-count badges (E3) — one batched fetch for the whole
  // project list per mount/refresh, never per row.
  const [suggestionCounts, setSuggestionCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (projects.length === 0) {
      setSuggestionCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = projects.map((p) => p.id);
      const counts: Record<string, number> = {};
      try {
        for (let i = 0; i < ids.length; i += 50) {
          const batch = ids.slice(i, i + 50);
          const res = await fetchWithActivity(`${API_BASE_URL}/api/workflows/suggestions/counts?projectIds=${batch.join(',')}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('riskninja-token')}` },
          });
          if (res.ok) Object.assign(counts, await res.json());
        }
        if (!cancelled) setSuggestionCounts(counts);
      } catch {
        // Non-critical — badges just won't show this refresh.
      }
    })();
    return () => { cancelled = true; };
  }, [projects]);

  // Badge click target: open the project directly on its Workflows tab,
  // regardless of whichever tab this session last remembered.
  const handleOpenWorkflows = (project: Project) => {
    onProjectSelect(project, { forceWorkflowsTab: true });
  };

  const childCountOf = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of projects) {
      const pid = p.parentId ?? null;
      if (pid) counts.set(pid, (counts.get(pid) || 0) + 1);
    }
    return counts;
  }, [projects]);

  const openCreate = (parentId: string | null, parentTitle?: string) => {
    setCreateParent({ id: parentId, title: parentTitle });
    setShowCreateModal(true);
  };

  // E17 guide navigator: `?newProject=1` opens the New Project modal once.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('newProject') !== '1') return;
    openCreate(null);
    const next = new URLSearchParams(searchParams);
    next.delete('newProject');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const drillInto = (project: Project) => {
    setCurrentParentId(project.id);
    setBreadcrumb((bc) => [...bc, { id: project.id, title: project.title }]);
  };

  const goToBreadcrumb = (index: number) => {
    // index -1 = root
    if (index < 0) {
      setCurrentParentId(null);
      setBreadcrumb([]);
      return;
    }
    const next = breadcrumb.slice(0, index + 1);
    setBreadcrumb(next);
    setCurrentParentId(next[next.length - 1].id);
  };

  const handleCreateProject = async (projectData: Partial<Project>) => {
    try {
      const newProject = await projectService.createProject({
        ...projectData,
        customerId,
        parentId: createParent.id,
        title: projectData.title!,
        type: projectData.type!
      } as any);
      setProjects((prev) => [newProject, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create project');
    }
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleDeleteProject = (project: Project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleSaveEdit = async (projectId: string, projectData: Partial<Project>) => {
    try {
      setActionLoading(true);
      const updatedProject = await projectService.updateProject(projectId, projectData);
      setProjects(projects.map(p => p.id === projectId ? { ...p, ...updatedProject } : p));
      setShowEditModal(false);
      setSelectedProject(null);
    } catch (err) {
      throw err; // Let the modal handle the error display
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async (projectId: string) => {
    try {
      setActionLoading(true);
      await projectService.deleteProject(projectId);
      // Remove the folder and (defensively) any descendants from local state.
      // The backend CASCADEs sub-folders; mirror that here so the UI stays in sync.
      const toRemove = new Set<string>([projectId]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const p of projects) {
          const pid = p.parentId ?? null;
          if (pid && toRemove.has(pid) && !toRemove.has(p.id)) {
            toRemove.add(p.id);
            grew = true;
          }
        }
      }
      setProjects(projects.filter(p => !toRemove.has(p.id)));
      setShowDeleteModal(false);
      setSelectedProject(null);
    } catch (err) {
      throw err; // Let the modal handle the error display
    } finally {
      setActionLoading(false);
    }
  };

  const matchesFilters = (p: Project) =>
    (filterStatus === 'all' || p.status === filterStatus) &&
    (filterType === 'all' || p.type === filterType);

  // Direct children of a folder (null = top level), after status/type filters
  const childrenOf = (parentId: string | null) =>
    projects.filter((p) => (p.parentId ?? null) === parentId && matchesFilters(p));

  // Projects at the current folder level
  const levelProjects = useMemo(
    () => childrenOf(currentParentId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, currentParentId, filterStatus, filterType]
  );

  // Group by status (card view only)
  const activeProjects = levelProjects.filter(p => p.status === 'active' || p.status === 'planning');
  const completedProjects = levelProjects.filter(p => p.status === 'completed');
  const otherProjects = levelProjects.filter(p => p.status === 'on_hold' || p.status === 'cancelled');

  const currentFolderTitle = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].title : undefined;

  const renderCard = (project: Project) => (
    <ProjectCard
      key={project.id}
      project={project}
      onClick={onProjectSelect}
      onEdit={handleEditProject}
      onDelete={handleDeleteProject}
      onDocumentsUploaded={() => fetchProjects()}
      childCount={childCountOf.get(project.id) || 0}
      onOpenSubfolders={drillInto}
      onAddSubfolder={(p) => openCreate(p.id, p.title)}
      suggestionCount={suggestionCounts[project.id] || 0}
      onOpenWorkflows={handleOpenWorkflows}
    />
  );

  // Recursively render a folder row and, when expanded, its sub-folder rows
  // nested beneath it (indented by depth). Returns a flat list of <tr>s.
  const renderListRows = (project: Project, depth: number): React.ReactNode[] => {
    const subCount = childCountOf.get(project.id) || 0;
    const isOpen = expanded.has(project.id);
    const rows: React.ReactNode[] = [
      <ProjectListRow
        key={project.id}
        project={project}
        depth={depth}
        subCount={subCount}
        isExpanded={isOpen}
        suggestionCount={suggestionCounts[project.id] || 0}
        onToggleExpand={toggleExpand}
        onSelect={onProjectSelect}
        onOpenWorkflows={handleOpenWorkflows}
        onAddSubfolder={(p) => openCreate(p.id, p.title)}
        onEdit={handleEditProject}
        onDelete={handleDeleteProject}
        onDocumentsUploaded={fetchProjects}
        onFilesDropped={(p, files, performUpload) => setActiveDrop({ project: p, files, performUpload })}
      />
    ];
    if (isOpen && subCount > 0) {
      for (const child of childrenOf(project.id)) {
        rows.push(...renderListRows(child, depth + 1));
      }
    }
    return rows;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>Error: {error}</p>
        <button
          onClick={fetchProjects}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const newButtonLabel = currentParentId ? 'New Sub-folder' : 'New Project';

  return (
    <div className="h-full flex flex-col">
      {showHolders && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowHolders(false)}>
          <div className="w-full max-w-xl max-h-[85vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-lg shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <CertificateHoldersPanel customerId={customerId} onClose={() => setShowHolders(false)} />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              aria-label="Back to customers"
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Customers</span>
            </button>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {customerName}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {levelProjects.length} {levelProjects.length === 1 ? 'folder' : 'folders'} here · {projects.length} total
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {features.acord && <button
              onClick={() => setShowHolders(true)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Saved certificate-of-insurance holders for this customer"
            >
              Certificate holders
            </button>}
            {/* Card / List view toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1" role="group" aria-label="View mode">
              <button
                onClick={() => setViewMode('card')}
                className={`px-2.5 py-1 rounded-md transition-colors ${viewMode === 'card' ? 'bg-white dark:bg-gray-800 shadow-sm text-primary' : 'text-gray-500 dark:text-gray-400'}`}
                title="Card view"
                aria-pressed={viewMode === 'card'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM13 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM13 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-2.5 py-1 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-800 shadow-sm text-primary' : 'text-gray-500 dark:text-gray-400'}`}
                title="List view"
                aria-pressed={viewMode === 'list'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* AMS360 Activity Button - only show if AMS360 enabled AND customer is linked */}
            {isAms360Enabled && ams360CustomerId && (
              <button
                onClick={() => setShowAMS360ActivityModal(true)}
                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1.5"
                title="Create activity in AMS360"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>AMS360 Activity</span>
              </button>
            )}

            <button
              onClick={() => openCreate(currentParentId, currentFolderTitle)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{newButtonLabel}</span>
            </button>
          </div>
        </div>

        {/* Breadcrumb trail */}
        <div className="mt-3 flex items-center gap-1 text-sm flex-wrap">
          <button
            onClick={() => goToBreadcrumb(-1)}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${currentParentId === null ? 'text-gray-900 dark:text-white font-medium' : 'text-primary'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            {customerName}
          </button>
          {breadcrumb.map((crumb, i) => (
            <React.Fragment key={crumb.id}>
              <span className="text-gray-300 dark:text-gray-600">/</span>
              <button
                onClick={() => goToBreadcrumb(i)}
                className={`px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${i === breadcrumb.length - 1 ? 'text-gray-900 dark:text-white font-medium' : 'text-primary'}`}
              >
                {crumb.title}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-4 flex space-x-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="pl-3 pr-7 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All Status</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="pl-3 pr-7 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All Types</option>
            <option value="new_business">New Business</option>
            <option value="renewal">Renewal</option>
            <option value="loss_run">Loss Run</option>
            <option value="year_over_year">Year Over Year</option>
            <option value="comparison">Comparison</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Project List */}
      <div className="flex-1 overflow-y-auto p-6">
        {levelProjects.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {currentParentId ? 'No sub-folders here' : 'No projects'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new {currentParentId ? 'sub-folder' : 'project'}.
            </p>
            <div className="mt-6">
              <button
                onClick={() => openCreate(currentParentId, currentFolderTitle)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {newButtonLabel}
              </button>
            </div>
          </div>
        ) : viewMode === 'list' ? (
          /* ---- List (file-explorer) view ---- */
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Created by</th>
                  <th className="px-4 py-2 font-medium">Modified</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {levelProjects.flatMap((project) => renderListRows(project, 0))}
              </tbody>
            </table>
          </div>
        ) : (
          /* ---- Card view ---- */
          <div className="space-y-6">
            {activeProjects.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Active</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeProjects.map(renderCard)}
                </div>
              </div>
            )}

            {completedProjects.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Completed</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {completedProjects.map(renderCard)}
                </div>
              </div>
            )}

            {otherProjects.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Other</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {otherProjects.map(renderCard)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          customerName={customerName}
          parentTitle={createParent.id ? createParent.title : undefined}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateProject}
        />
      )}

      {/* Edit Project Modal */}
      <EditProjectModal
        project={selectedProject}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProject(null);
        }}
        onSave={handleSaveEdit}
        loading={actionLoading}
      />

      {/* Delete Project Modal */}
      <DeleteProjectModal
        project={selectedProject}
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedProject(null);
        }}
        onConfirm={handleConfirmDelete}
        loading={actionLoading}
      />

      {/* AMS360 Activity Modal */}
      <AMS360ActivityModal
        isOpen={showAMS360ActivityModal}
        onClose={() => setShowAMS360ActivityModal(false)}
        customerId={customerId}
        customerName={customerName}
      />

      {/* Shared rename modal for list-view row drops (one per list, not per row) */}
      <BatchRenameModal
        isOpen={!!activeDrop}
        files={activeDrop?.files || []}
        projectTitle={activeDrop?.project.title || ''}
        onCancel={() => setActiveDrop(null)}
        onConfirm={async (rows) => {
          const drop = activeDrop;
          setActiveDrop(null);
          if (drop) await drop.performUpload(rows);
        }}
      />
    </div>
  );
};

export default ProjectManagement;
